import { z } from 'zod';
import { CLINIC_SECTIONS } from './access.js';
import {
  AmountRialSchema,
  BookingInstantSchema,
  DateOnlySchema,
  IsoDateTimeSchema,
  RialStringSchema,
  UuidSchema,
  idListQuery,
  optionalText,
  pageSchema,
} from './common.js';

/** Service colours come only from a tested palette (CLAUDE.md §10, ADR-025). */
export const SERVICE_COLORS = ['lilac', 'rose', 'mint', 'sky', 'sand', 'clay'] as const;
export type ServiceColor = (typeof SERVICE_COLORS)[number];

export const RESOURCE_KINDS = ['room', 'device'] as const;
export type ResourceKind = (typeof RESOURCE_KINDS)[number];

const CategorySchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9_]{1,40}$/, { message: 'دسته فقط حروف لاتین کوچک، عدد و _ است.' });

/** ADR-017 / ADR-027: a service may need one room and/or a device of a category. */
export const ResourceRequirementSchema = z.object({
  kind: z.enum(RESOURCE_KINDS),
  category: CategorySchema.nullish().transform((v) => v ?? null),
});
export type ResourceRequirement = z.infer<typeof ResourceRequirementSchema>;

const NameSchema = z
  .string()
  .trim()
  .min(1, { message: 'نام را وارد کنید.' })
  .max(80, { message: 'نام حداکثر ۸۰ نویسه است.' });

export const ServiceSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  durationMinutes: z.number().int(),
  materialCostRial: RialStringSchema,
  color: z.enum(SERVICE_COLORS),
  recallAfterDays: z.number().int().nullable(),
  /** The service is touched up a while after it is done; the days are each practitioner's (ADR-052). */
  touchUp: z.boolean(),
  resourceRequirements: z.array(ResourceRequirementSchema),
  treatmentTemplateId: UuidSchema.nullable(),
  /** The clinic section the service belongs to (ADR-036); its appointments and charges copy it. */
  section: z.enum(CLINIC_SECTIONS),
  status: z.enum(['active', 'archived']),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type Service = z.infer<typeof ServiceSchema>;

export const ServiceInputSchema = z.object({
  name: NameSchema,
  durationMinutes: z.coerce
    .number()
    .int()
    .min(5, { message: 'مدت دست‌کم ۵ دقیقه است.' })
    .max(600, { message: 'مدت حداکثر ۶۰۰ دقیقه است.' }),
  materialCostRial: AmountRialSchema.default('0'),
  color: z.enum(SERVICE_COLORS).default('lilac'),
  recallAfterDays: z.coerce
    .number()
    .int()
    .min(1)
    .max(3650)
    .nullish()
    .transform((v) => v ?? null),
  touchUp: z.boolean().default(false),
  resourceRequirements: z
    .array(ResourceRequirementSchema)
    .max(4)
    .refine((v) => new Set(v.map((r) => `${r.kind}:${r.category ?? ''}`)).size === v.length, {
      message: 'نیاز تکراری است.',
    })
    .default([]),
  treatmentTemplateId: UuidSchema.nullish().transform((v) => v ?? null),
  section: z.enum(CLINIC_SECTIONS).default('general'),
});
export type ServiceInput = z.input<typeof ServiceInputSchema>;

export const ResourceSchema = z.object({
  id: UuidSchema,
  kind: z.enum(RESOURCE_KINDS),
  name: z.string(),
  category: z.string().nullable(),
  status: z.enum(['active', 'archived']),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type Resource = z.infer<typeof ResourceSchema>;

export const ResourceInputSchema = z.object({
  kind: z.enum(RESOURCE_KINDS),
  name: NameSchema,
  category: CategorySchema.nullish().transform((v) => v ?? null),
});
export type ResourceInput = z.input<typeof ResourceInputSchema>;

export const ArchivedFilterQuerySchema = z.object({
  includeArchived: z.enum(['true', 'false']).default('false').transform((v) => v === 'true'),
});

export const ServiceListResponseSchema = z.object({ items: z.array(ServiceSchema) });
export const ResourceListResponseSchema = z.object({ items: z.array(ResourceSchema) });

export const PractitionerSchema = z.object({
  membershipId: UuidSchema,
  displayName: z.string().nullable(),
  /** The doctor allowed overlapping appointments for themselves (ADR-034). */
  allowsOverlap: z.boolean(),
  /** Sections this member may treat in: a doctor all, a laser operator laser only (ADR-036). */
  sections: z.array(z.enum(CLINIC_SECTIONS)),
  /** Days after a touch-up service when its touch-up is due, this practitioner's own (ADR-052). */
  touchUpDays: z.number().int(),
});
export type Practitioner = z.infer<typeof PractitionerSchema>;

/** Touch-up days when a practitioner has not set theirs, or the service was done outside the clinic. */
export const DEFAULT_TOUCH_UP_DAYS = 14;
export const PractitionerListResponseSchema = z.object({ items: z.array(PractitionerSchema) });

/** A doctor's own booking preferences (ADR-034); only that doctor reads them here and writes them. */
export const PractitionerSettingsSchema = z.object({
  membershipId: UuidSchema,
  allowOverlap: z.boolean(),
  touchUpDays: z.number().int(),
  updatedAt: IsoDateTimeSchema.nullable(),
});
export type PractitionerSettings = z.infer<typeof PractitionerSettingsSchema>;

export const PractitionerSettingsInputSchema = z.object({
  allowOverlap: z.boolean(),
  /** Left out: unchanged (ADR-052). */
  touchUpDays: z.coerce
    .number()
    .int()
    .min(1, { message: 'روز ترمیم دست‌کم ۱ است.' })
    .max(365, { message: 'روز ترمیم حداکثر ۳۶۵ است.' })
    .optional(),
});
export type PractitionerSettingsInput = z.input<typeof PractitionerSettingsInputSchema>;

/** The clinic's booking settings (ADR-035): whether the desk checks a patient in before the visit. */
/**
 * The recall list's window (BR-63): how many days after a recall date a patient stays on it, and
 * how many days before it they appear (0 = only once it is due). Set by the clinic admin.
 */
export const RECALL_WINDOW = {
  overdue: { min: 1, max: 3650, default: 180 },
  ahead: { min: 0, max: 90, default: 14 },
} as const;

/**
 * The clinic's working days and hours (migration 0042): seven entries, Saturday first; minutes into the
 * Tehran day, on a quarter-hour grid. A closed day has no hours.
 */
export const WorkingDaySchema = z
  .object({
    open: z.boolean(),
    from: z.number().int().min(0).max(1440),
    to: z.number().int().min(0).max(1440),
  })
  .refine((d) => !d.open || (d.from < d.to && d.from % 15 === 0 && d.to % 15 === 0), {
    message: 'ساعت پایان باید پس از شروع باشد (مضرب ۱۵ دقیقه).',
  });
export type WorkingDay = z.infer<typeof WorkingDaySchema>;
export const WorkingHoursSchema = z.array(WorkingDaySchema).length(7);
export type WorkingHours = z.infer<typeof WorkingHoursSchema>;
/** What a clinic that never set hours has. */
export const DEFAULT_WORKING_HOURS: WorkingHours = Array.from({ length: 7 }, () => ({ open: true, from: 8 * 60, to: 21 * 60 }));

/**
 * Named shifts (migration 0043, employer request 2026-10-13): each shift is a full week like above,
 * named by the admin («شیفت صبح»، «شیفت دکتر ...»). A time is open when any shift covers it.
 */
export const WORKING_SHIFTS_MAX = 10;
export const SHIFT_NAME_MAX = 40;
export const WorkingShiftSchema = z.object({
  name: z.string().trim().min(1, { message: 'نام شیفت را بنویسید.' }).max(SHIFT_NAME_MAX, { message: 'نام شیفت حداکثر ۴۰ نویسه است.' }),
  days: WorkingHoursSchema,
});
export type WorkingShift = z.infer<typeof WorkingShiftSchema>;
export const WorkingShiftsSchema = z
  .array(WorkingShiftSchema)
  .min(1, { message: 'دست‌کم یک شیفت لازم است.' })
  .max(WORKING_SHIFTS_MAX, { message: 'حداکثر ۱۰ شیفت.' })
  .refine((l) => new Set(l.map((x) => x.name)).size === l.length, { message: 'نام شیفت‌ها نباید تکراری باشد.' });
export type WorkingShifts = z.infer<typeof WorkingShiftsSchema>;
export const DEFAULT_WORKING_SHIFTS: WorkingShifts = [{ name: 'شیفت اصلی', days: DEFAULT_WORKING_HOURS }];

/**
 * Dates the clinic is closed (a holiday, a staff day off; ADR-059). The first-free suggestion skips
 * them and the calendar marks them; a booking on one is still accepted.
 */
export const CLOSED_DAYS_MAX = 200;
export const ClosedDaysSchema = z
  .array(DateOnlySchema)
  .max(CLOSED_DAYS_MAX, { message: 'حداکثر ۲۰۰ روز تعطیل.' })
  .refine((l) => new Set(l).size === l.length, { message: 'این روز قبلاً اضافه شده است.' });

export const BookingSettingsSchema = z.object({
  workingShifts: WorkingShiftsSchema,
  closedDays: ClosedDaysSchema,
  checkInRequired: z.boolean(),
  recallOverdueDays: z.number().int(),
  recallAheadDays: z.number().int(),
  updatedAt: IsoDateTimeSchema.nullable(),
});
export type BookingSettings = z.infer<typeof BookingSettingsSchema>;

/** Every field is optional and a missing one is kept, so one card never resets another's value. */
export const BookingSettingsInputSchema = z
  .object({
    checkInRequired: z.boolean().optional(),
    recallOverdueDays: z.coerce
      .number()
      .int({ message: 'عدد صحیح وارد کنید.' })
      .min(RECALL_WINDOW.overdue.min, { message: 'دست‌کم ۱ روز.' })
      .max(RECALL_WINDOW.overdue.max, { message: 'حداکثر ۳۶۵۰ روز.' })
      .optional(),
    workingShifts: WorkingShiftsSchema.optional(),
    closedDays: ClosedDaysSchema.optional(),
    recallAheadDays: z.coerce
      .number()
      .int({ message: 'عدد صحیح وارد کنید.' })
      .min(RECALL_WINDOW.ahead.min, { message: 'نمی‌تواند منفی باشد.' })
      .max(RECALL_WINDOW.ahead.max, { message: 'حداکثر ۹۰ روز.' })
      .optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), { message: 'تغییری فرستاده نشد.' });
export type BookingSettingsInput = z.input<typeof BookingSettingsInputSchema>;

export const APPOINTMENT_STATUSES = [
  'scheduled',
  'confirmed',
  'checked_in',
  'in_progress',
  'completed',
  'cancelled',
  'no_show',
] as const;
export type AppointmentStatus = (typeof APPOINTMENT_STATUSES)[number];

/** Statuses that hold the practitioner's and the resources' time. */
export const ACTIVE_APPOINTMENT_STATUSES = ['scheduled', 'confirmed', 'checked_in', 'in_progress'] as const;

export const AppointmentPatientSchema = z.object({
  id: UuidSchema,
  firstName: z.string(),
  lastName: z.string().nullable(),
  phone: z.string().nullable(),
});

export const AppointmentSchema = z.object({
  id: UuidSchema,
  patient: AppointmentPatientSchema,
  practitionerMembershipId: UuidSchema,
  serviceId: UuidSchema,
  /** Copied from the service when booked (ADR-036). */
  section: z.enum(CLINIC_SECTIONS),
  startsAt: IsoDateTimeSchema,
  endsAt: IsoDateTimeSchema,
  status: z.enum(APPOINTMENT_STATUSES),
  overlapOverride: z.boolean(),
  /** Booked by the desk for a patient already there (ADR-035). */
  walkIn: z.boolean(),
  overrideReason: z.string().nullable(),
  note: z.string().nullable(),
  cancelReason: z.string().nullable(),
  resourceIds: z.array(UuidSchema),
  smsTemplateIds: z.array(UuidSchema).nullable(),
  statusChangedAt: IsoDateTimeSchema,
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type Appointment = z.infer<typeof AppointmentSchema>;

const DurationSchema = z.coerce
  .number()
  .int()
  .min(5, { message: 'مدت دست‌کم ۵ دقیقه است.' })
  .max(720, { message: 'مدت حداکثر ۱۲ ساعت است.' });

const ResourceIdsSchema = z
  .array(UuidSchema)
  .max(4)
  .refine((v) => new Set(v).size === v.length, { message: 'مورد تکراری است.' });

export const AppointmentCreateInputSchema = z.object({
  patientId: UuidSchema,
  practitionerMembershipId: UuidSchema,
  serviceId: UuidSchema,
  startsAt: BookingInstantSchema,
  /** Defaults to the service duration. */
  durationMinutes: DurationSchema.optional(),
  resourceIds: ResourceIdsSchema.default([]),
  note: optionalText(500),
  /** null or missing: all timed SMS templates (legacy `smsTpls`). */
  smsTemplateIds: z
    .array(UuidSchema)
    .max(10)
    .nullish()
    .transform((v) => v ?? null),
});
export type AppointmentCreateInput = z.input<typeof AppointmentCreateInputSchema>;

export const AppointmentRescheduleInputSchema = z.object({
  startsAt: BookingInstantSchema,
  durationMinutes: DurationSchema.optional(),
  practitionerMembershipId: UuidSchema.optional(),
  serviceId: UuidSchema.optional(),
  resourceIds: ResourceIdsSchema.optional(),
});
export type AppointmentRescheduleInput = z.input<typeof AppointmentRescheduleInputSchema>;

/**
 * A patient who came without an appointment (ADR-035): the server books the doctor's first free
 * time from now until the end of today, and checks the patient in when the clinic uses check-in.
 */
export const WalkInInputSchema = z.object({
  patientId: UuidSchema,
  practitionerMembershipId: UuidSchema,
  serviceId: UuidSchema,
  resourceIds: ResourceIdsSchema.default([]),
  note: optionalText(500),
});
export type WalkInInput = z.input<typeof WalkInInputSchema>;

export const AppointmentCancelInputSchema = z.object({
  reason: optionalText(300),
});
export type AppointmentCancelInput = z.input<typeof AppointmentCancelInputSchema>;

export const APPOINTMENT_RANGE_MAX_DAYS = 31;

/** Either a time range (calendar, max 31 days) or one patient's history (newest first, paged). */
export const AppointmentListQuerySchema = z
  .object({
    from: IsoDateTimeSchema.optional(),
    to: IsoDateTimeSchema.optional(),
    practitionerId: UuidSchema.optional(),
    section: z.enum(CLINIC_SECTIONS).optional(),
    patientId: UuidSchema.optional(),
    cursor: z.uuid().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
  })
  .superRefine((q, ctx) => {
    const ranged = q.from !== undefined || q.to !== undefined;
    if (ranged) {
      if (!q.from || !q.to) {
        ctx.addIssue({ code: 'custom', path: ['to'], message: 'بازه‌ی زمانی کامل نیست.' });
        return;
      }
      const span = Date.parse(q.to) - Date.parse(q.from);
      if (span <= 0 || span > APPOINTMENT_RANGE_MAX_DAYS * 86_400_000) {
        ctx.addIssue({ code: 'custom', path: ['to'], message: 'بازه‌ی زمانی حداکثر ۳۱ روز است.' });
      }
    } else if (!q.patientId) {
      ctx.addIssue({ code: 'custom', path: ['from'], message: 'بازه‌ی زمانی یا کلاینت را مشخص کنید.' });
    }
  });
export type AppointmentListQuery = z.infer<typeof AppointmentListQuerySchema>;

/** The days of a calendar month (Tehran, both ends included, at most 31 days) and the calendar's filters. */
export const AppointmentDayCountsQuerySchema = z
  .object({
    from: DateOnlySchema,
    to: DateOnlySchema,
    practitionerId: UuidSchema.optional(),
    section: z.enum(CLINIC_SECTIONS).optional(),
  })
  .superRefine((q, ctx) => {
    const days = (Date.parse(q.to) - Date.parse(q.from)) / 86_400_000 + 1;
    if (days < 1 || days > APPOINTMENT_RANGE_MAX_DAYS) {
      ctx.addIssue({ code: 'custom', path: ['to'], message: 'بازه‌ی زمانی حداکثر ۳۱ روز است.' });
    }
  });
export type AppointmentDayCountsQuery = z.infer<typeof AppointmentDayCountsQuerySchema>;

/**
 * How many patients have an appointment on each day (cancelled ones left out), in the caller's
 * sections. Days with none are not listed.
 */
export const AppointmentDayCountsSchema = z.object({
  items: z.array(z.object({ date: DateOnlySchema, patients: z.number().int().nonnegative() })),
});
export type AppointmentDayCounts = z.infer<typeof AppointmentDayCountsSchema>;

/**
 * Time taken by an appointment the caller cannot see (another section, ADR-036): when, by whom and
 * which rooms or devices. Never the patient, the service or the appointment itself.
 */
export const BusyBlockSchema = z.object({
  practitionerMembershipId: UuidSchema,
  resourceIds: z.array(UuidSchema),
  startsAt: IsoDateTimeSchema,
  endsAt: IsoDateTimeSchema,
});
export type BusyBlock = z.infer<typeof BusyBlockSchema>;

export const AppointmentListResponseSchema = pageSchema(AppointmentSchema).extend({
  /** Only for a time range; empty for one patient's history. */
  busy: z.array(BusyBlockSchema),
});
export type AppointmentListResponse = z.infer<typeof AppointmentListResponseSchema>;

/** At most this many coming appointments per patient in `GET /appointments/upcoming`. */
export const UPCOMING_PER_PATIENT = 5;

/** The coming appointments of some patients (still holding time, not over), soonest first. */
export const UpcomingAppointmentsResponseSchema = z.object({ items: z.array(AppointmentSchema) });
export type UpcomingAppointmentsResponse = z.infer<typeof UpcomingAppointmentsResponseSchema>;

/**
 * Where a free time is looked for (BR-62), in minutes into a Tehran day: starts on a 15-minute
 * grid from 08:00, and the appointment ends by 21:00. The calendar shows the same hours.
 */
export const BOOKING_HOURS = { startMinute: 8 * 60, endMinute: 21 * 60, slotMinutes: 15, scanDays: 31 } as const;

export const AvailabilityQuerySchema = z.object({
  practitionerMembershipId: UuidSchema,
  serviceId: UuidSchema,
  date: DateOnlySchema,
  /** Defaults to the service duration. */
  durationMinutes: DurationSchema.optional(),
  resourceIds: idListQuery(4).optional(),
});
export type AvailabilityQuery = z.infer<typeof AvailabilityQuerySchema>;

export const BUSY_REASONS = ['practitioner', 'resource'] as const;
export type BusyReason = (typeof BUSY_REASONS)[number];

/**
 * A booking aid (BR-62), never a promise: the time already taken on `date` and the first free
 * start from that day (not before now) within `BOOKING_HOURS.scanDays`. Booking still decides.
 */
export const AvailabilitySchema = z.object({
  date: DateOnlySchema,
  busy: z.array(z.object({ startsAt: IsoDateTimeSchema, endsAt: IsoDateTimeSchema, reason: z.enum(BUSY_REASONS) })),
  nextFree: IsoDateTimeSchema.nullable(),
});
export type Availability = z.infer<typeof AvailabilitySchema>;

/** At most this many rows in the recall list; more is said with `truncated`. */
export const RECALL_LIST_LIMIT = 500;

/**
 * A client due for a service again (BR-63), or for its touch-up (ADR-052): the latest time they had
 * that service, from a completed visit or a recorded past service, and the date stored on it. No
 * treatment record.
 */
export const RecallItemSchema = z.object({
  /** The visit line or past service the date is on; closing the follow-up is recorded against it. */
  sourceId: UuidSchema,
  patient: AppointmentPatientSchema,
  serviceId: UuidSchema,
  section: z.enum(CLINIC_SECTIONS),
  /** For a past service: its day, at the start of that day in Tehran. */
  completedAt: IsoDateTimeSchema,
  dueOn: DateOnlySchema,
  /** Recorded by hand as a past service, not a visit here. */
  prior: z.boolean(),
});
export type RecallItem = z.infer<typeof RecallItemSchema>;

export const RecallListResponseSchema = z.object({
  /** Overdue ones nearest to today first, then the coming ones soonest first. */
  items: z.array(RecallItemSchema),
  /** The clinic's day the list was made for (Tehran). */
  today: DateOnlySchema,
  overdueDays: z.number().int(),
  aheadDays: z.number().int(),
  truncated: z.boolean(),
});
export type RecallListResponse = z.infer<typeof RecallListResponseSchema>;
